<template>
	<router-link
		:to="navigateTo"
		@click.native="closeMenu"
		:class="{ selected: isSelected, 'pad-right': project.overrideSuffixComponent, 'menu-active': menuOpen }"
		class="width-100 display-flex flex-row flex-align-center flex-justify-space-between project-menu-item-container"
	>
		<div class="display-flex flex-row flex-align-center">
			<component class="mr-1" v-if="project.overridePrefixComponent" :is="project.overridePrefixComponent" />
			<div v-else-if="!hideIcon" class="mr-2">
				<SpaceIconCard>
					<AppIcon :name="projectIconIndicatorName" width="10" />
				</SpaceIconCard>
			</div>
			<div class="item-name one-row">
				{{ title }}
			</div>
			<div class="additional-info">
				{{ project.additionalInfo }}
			</div>
		</div>
		<component v-if="project.overrideSuffixComponent" :is="project.overrideSuffixComponent" />
		<ProjectMenuSettingsDropdown v-model="menuOpen" :project="project" class="settings-button" v-else-if="showSetting" />
	</router-link>
</template>

<script>
import { mapGetters } from 'vuex';

import { ProjectPermissions } from '@ply-io/ply-front-shared-utilities';

import ProjectMenuSettingsDropdown from '@/components/ProjectMenu/ProjectMenuSettingsDropdown.vue';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { getProject, projectHasPermission } from '@/store/modules/projects/getters';
import { selectedProjectId } from '@/store/modules/selectedProject/getters';
import ProjectService from '@/utils/projects-service';
import { VIRTUAL_PROJECT_IDS, ALL_PROJECTS_VIRTUAL_ID, PROJECTS_SHARED_WITH_ME_VIRTUAL_ID } from '@/utils/projects.const';
import SpaceIconCard from '@/views/AppLayout/ManageAndCreate/SpaceIconCard.vue';
export default {
	name: 'ProjectMenuItem',
	components: {
		SpaceIconCard,
		ProjectMenuSettingsDropdown,
	},
	props: {
		project: { type: Object },
		hideIcon: { type: Boolean },
	},
	computed: {
		...mapGetters([getProject.fullPath, selectedProjectId.fullPath, projectHasPermission.fullPath]),
		projectIdentifier() {
			const identifier = this[selectedProjectId.fullPath];
			return isNaN(identifier) ? identifier.toLowerCase() : identifier;
		},
		projectIconIndicatorName() {
			if (this.isEverythingProject) {
				return 'all-projects';
			} else if (this.isSharedWithMeProject) {
				return 'shared-with-me';
			} else {
				return `${this.isPersonalProject ? 'personal' : 'collaborative'}-space`;
			}
		},
		isPersonalProject() {
			return ProjectService.isPersonalProject(this.project);
		},
		isEverythingProject() {
			return this.project.id === ALL_PROJECTS_VIRTUAL_ID;
		},
		isSharedWithMeProject() {
			return this.project.id === PROJECTS_SHARED_WITH_ME_VIRTUAL_ID;
		},
		selectedProject() {
			if (!this.projectIdentifier) {
				return null;
			}
			if (VIRTUAL_PROJECT_IDS.includes(this.projectIdentifier)) {
				return { id: this.projectIdentifier };
			}
			return this[getProject.fullPath](this.projectIdentifier);
		},
		navigateTo() {
			const params = { ...this.$route.params, spaceId: this.project.id };
			return { params };
		},
		isSelected() {
			return Boolean(this.selectedProject) && this.selectedProject.id === this.project.id && !this.isProjectsRoot;
		},
		isProjectsRoot() {
			return this.$route?.meta?.isProjectsHome;
		},
		title() {
			return this.project.title || '';
		},
		showSetting() {
			return !!this.project.id && this.hasPermissionToSeeSettings;
		},
		hasPermissionToSeeSettings() {
			return this[projectHasPermission.fullPath](this.project.id, ProjectPermissions.ViewSettings);
		},
	},
	methods: {
		closeMenu() {
			this.$emit('close');
		},
		openSettings() {
			AppDialogManagerService.openProjectSettingsDialog({ project: this.project });
		},
	},
	data() {
		return {
			menuOpen: false,
		};
	},
};
</script>

<style scoped lang="scss">
.opened-directory {
	display: none !important;
}
.closed-directory {
	display: flex !important;
}
.project-menu-item-container {
	height: 26px;
	cursor: pointer;
	padding-left: 6px;
	&.pad-right {
		padding-right: 12px;
	}
	border-radius: 7px;
	text-decoration: none;
	&.selected {
		background: #f4f5f6;
		display: flex;
		.opened-directory {
			display: flex !important;
		}
		.closed-directory {
			display: none !important;
		}
		.item-name {
			font-weight: 600;
		}
	}
	&:hover,
	&.menu-active {
		background: #f4f5f66c;
		.settings-button {
			opacity: 1;
			transition: 0.2s;
		}
	}
}
.item-name {
	font-size: 14px;
	font-weight: normal;
	color: var(--primary-font-color);
}
.settings-button {
	opacity: 0;
	transition: 0.2s;
}
.additional-info {
	font-size: 12px;
	color: #9095ac;
	margin: 1px 0 0 6px;
}
</style>
